/**
 * Детектор самовосстановления видео переговорки.
 *
 * На каждом тике получает framesDecoded и текущий sendVideo. Freeze трактуется
 * как «sendVideo: true, но framesDecoded не растёт дольше NO_FRAME_THRESHOLD_MS».
 * Когда кадры возобновляются — эмитит дельту (успех самовосстановления).
 *
 * Discard важен: если во время freeze участник сам выключил камеру
 * (sendVideo: true → false), такой кейс не считается самовосстановлением и
 * не должен попадать в RUM-квантили — откидываем начатую дельту.
 */
import { ACTION, FEATURE } from '../../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../../shared/helpers/loggerFacade';
import { FREEZE_DELTA_TIMEOUT_MS, NO_FRAME_THRESHOLD_MS } from '../constants';
import type { FreezeTickInput } from '../types';

const logger = createLogger('transcoder-observability:freeze');

type PeerFreezeState = {
    lastFramesDecoded: number | null;
    lastFrameSeenMs: number | null;
    inFreeze: boolean;
    lastSendVideo: boolean;
};

export class FreezeDetector {
    private states = new Map<string, PeerFreezeState>();

    public tick({ peerId, framesDecoded, sendVideo, nowMs, participantsCount }: FreezeTickInput): void {
        const state = this.states.get(peerId) ?? {
            lastFramesDecoded: null,
            lastFrameSeenMs: null,
            inFreeze: false,
            lastSendVideo: sendVideo,
        };

        const framesAdvanced = state.lastFramesDecoded !== null && framesDecoded > state.lastFramesDecoded;

        if (framesAdvanced) {
            state.lastFrameSeenMs = nowMs;
            if (state.inFreeze) {
                logger.endFeatureDelta({
                    feature: FEATURE.CONFERENCE,
                    action: ACTION.TRANSCODER_VIDEO_FREEZE,
                    id: peerId,
                    isSuccess: true,
                });
                state.inFreeze = false;
            }
        }

        const turnedOff = state.lastSendVideo && !sendVideo;
        if (turnedOff && state.inFreeze) {
            logger.discardFeatureDelta({
                feature: FEATURE.CONFERENCE,
                action: ACTION.TRANSCODER_VIDEO_FREEZE,
                id: peerId,
            });
            state.inFreeze = false;
            state.lastFrameSeenMs = null;
        }

        const turnedOn = !state.lastSendVideo && sendVideo;
        if (turnedOn) {
            state.lastFrameSeenMs = nowMs;
        }

        if (!framesAdvanced && sendVideo && !state.inFreeze) {
            if (state.lastFrameSeenMs === null) {
                state.lastFrameSeenMs = nowMs;
            }
            const silenceMs = nowMs - state.lastFrameSeenMs;
            if (silenceMs > NO_FRAME_THRESHOLD_MS) {
                logger.startFeatureDelta({
                    feature: FEATURE.CONFERENCE,
                    action: ACTION.TRANSCODER_VIDEO_FREEZE,
                    id: peerId,
                    timeoutMS: FREEZE_DELTA_TIMEOUT_MS,
                    options: {
                        additional: {
                            transcoder_peer_id: peerId,
                            participants_count: participantsCount,
                        },
                    },
                });
                state.inFreeze = true;
            }
        }

        if (state.lastFramesDecoded === null || framesAdvanced) {
            state.lastFramesDecoded = framesDecoded;
        }
        state.lastSendVideo = sendVideo;
        this.states.set(peerId, state);
    }

    public drop(peerId: string): void {
        const state = this.states.get(peerId);
        if (!state) {
            return;
        }
        if (state.inFreeze) {
            logger.discardFeatureDelta({
                feature: FEATURE.CONFERENCE,
                action: ACTION.TRANSCODER_VIDEO_FREEZE,
                id: peerId,
            });
        }
        this.states.delete(peerId);
    }

    public clear(): void {
        for (const peerId of Array.from(this.states.keys())) {
            this.drop(peerId);
        }
    }

    public has(peerId: string): boolean {
        return this.states.has(peerId);
    }
}
